import { Info as TabIconInfo, FileJson as TabIconFileJson } from "lucide-react";
import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDateTime } from "src/utils/dateTime";
import { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, X } from "lucide-react";
import styled from "styled-components";
import Panel from "@ui/panel/Panel";
import Pre from "@ui/pre/Pre";
import { TabItem, Tabs } from "@ui/tabs/Tabs";
import { GD } from "src/GD";
import { AdminButton } from "../../pages/administration/AdminControls";

type RecordData = Record<string, unknown>;
const object = (value: unknown): RecordData | null => value !== null && typeof value === "object" && !Array.isArray(value) ? value as RecordData : null;
const text = (value: unknown): string => value == null || value === "" ? "—" : typeof value === "object" ? JSON.stringify(value) : String(value);
const readReco = (value: unknown) => {
	const envelope = object(value);
	return object(envelope?.data) ?? envelope;
};
const dateText = (value: unknown, language: string) => {
	if (typeof value !== "string") return text(value);
	return formatDisplayDateTime(value, { locale: language, fallback: value });
};

const Header = styled.div`
	flex: 0 0 auto; padding: 20px 24px 16px; border-bottom: 1px solid #edf0f5; background: #fff;
	font: 13px/1.5 "Euclid Circular B", sans-serif; color: #344054;
	display: flex; align-items: flex-start; justify-content: space-between; gap: 20px;
	.eyebrow { color: #667085; font-size: 11px; margin-bottom: 5px; }
	h2 { margin: 0; font-size: 21px; font-weight: 600; line-height: 1.3; color: #182230; }
	button.close { width: 32px; height: 32px; padding: 0; border-color: transparent; color: #667085; }
`;
const TabArea = styled.div`
	display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0;
	> div:first-child { flex: 0 0 auto; }
`;
const Body = styled.div`
	overflow-y: auto; min-height: 0; flex: 1 1 auto; padding: 20px 24px 24px; background: #f5f7fa;
	font: 13px/1.5 "Euclid Circular B", sans-serif; letter-spacing: 0; color: #344054;
	.section-label { margin: 24px 0 10px; color: #667085; font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; }
	.section-label:first-child { margin-top: 0; }
	.card { padding: 18px 20px; border: 1px solid #d0d5dd; border-radius: 12px; background: #fff; box-shadow: 0 1px 2px rgba(16,24,40,.05); min-width: 0; }
	.step + .step { margin-top: 16px; }
	.step-head { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; margin-bottom: 18px; }
	h3 { margin: 0; color: #182230; font-size: 15px; font-weight: 600; }
	h4 { margin: 18px 0 10px; color: #667085; font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; }
	.badge { padding: 2px 8px; border: 1px solid #d0d5dd; border-radius: 6px; font-size: 12px; background: #f9fafb; }
	.badge.finished { color: #18774d; background: #f0f9f4; border-color: #a9d9be; }
	.badge.active { color: #175cd3; background: #eff4ff; border-color: #b2ccff; }
	.badge.failed, .error { color: #b42318; }
	.fields { display: grid; grid-template-columns: minmax(130px, 32%) minmax(0, 1fr); gap: 8px 16px; margin: 0; }
	dt { color: #667085; overflow-wrap: anywhere; }
	dd { margin: 0; min-width: 0; color: #182230; overflow-wrap: anywhere; white-space: pre-wrap; }
	.nested { margin-top: 14px; padding-top: 14px; border-top: 1px solid #eaecf0; }
	.nested > .field-name { color: #667085; margin-bottom: 10px; overflow-wrap: anywhere; }
	.muted { color: #98a2b3; }
	details { margin-top: 18px; border-top: 1px solid #eaecf0; padding-top: 14px; }
	summary { display: flex; align-items: center; gap: 8px; cursor: pointer; list-style: none; color: #475467; }
	summary::-webkit-details-marker { display: none; }
	details[open] > summary { margin-bottom: 12px; }
	details[open] > summary svg { transform: rotate(180deg); }
	@media (max-width: 600px) {
		padding: 16px; .card { padding: 16px; }
		.fields { grid-template-columns: minmax(0, 1fr); gap: 3px; }
		dd + dt { margin-top: 9px; }
	}
`;

/** Preserve field names and decimal strings exactly as supplied by the processor. */
const Fields = ({ value }: { value: unknown }) => {
	useLocale();
	const record = object(value);
	if (!record) return <span>{text(value)}</span>;
	const entries = Object.entries(record);
	if (!entries.length) return <span className="muted">—</span>;
	return <>
		<dl className="fields">{entries.filter(([, item]) => !object(item)).map(([key, item]) =>
			<Fragment key={key}><dt>{key}</dt><dd>{text(item)}</dd></Fragment>
		)}</dl>
		{entries.filter(([, item]) => object(item)).map(([key, item]) =>
			<div className="nested" key={key}><div className="field-name">{key}</div><Fields value={item} /></div>
		)}
	</>;
};

export const RecoDetailsPopup = ({ id, onClose }: { id: number; onClose: () => void }) => {
	useLocale();
	const { i18n } = useTranslation();
	const ru = i18n.language.startsWith("ru");
	const label = (en: string, russian: string) => ru ? russian : en;
	const popup = useRef<HTMLDivElement>(null);
	const [data, setData] = useState<unknown>();
	const [error, setError] = useState<string>();
	const [busy, setBusy] = useState(true);
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		const previousFocus = document.activeElement;
		popup.current?.focus({ preventScroll: true });
		return () => {
			if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true });
		};
	}, []);

	useEffect(() => {
		let active = true;
		setBusy(true);
		setError(undefined);
		setData(undefined);

		const load = async () => {
			try {
				const response = await GD.REQ_API.request({
					method: `processing/reco/${id}`,
					data: {},
				});
				if (!active) return;
				if (response.error) {
					setError(response.error.message || msg("couldNotLoadRecoDetails"));
				} else {
					setData(response.data);
				}
			} catch {
				if (active) setError(msg("couldNotLoadRecoDetails"));
			} finally {
				if (active) setBusy(false);
			}
		};

		void load();
		return () => { active = false; };
	}, [id, attempt]);

	const record = readReco(data);
	const steps = Array.isArray(record?.data) ? record.data : [];
	const closeLabel = label("Close", msg("message_a7a40336"));
	return createPortal(<div ref={popup} role="dialog" aria-modal="true" aria-label={msg("templateReco_2d457391", { v0: id })} tabIndex={-1} onKeyDown={event => {
		if (event.key === "Escape") { event.stopPropagation(); onClose(); }
		if (event.key !== "Tab") return;
		const controls = Array.from(popup.current?.querySelectorAll<HTMLElement>('button:not(:disabled), summary, [href], [tabindex="0"]') ?? []).filter(element => element.getClientRects().length > 0);
		if (!controls.length) { event.preventDefault(); return; }
		const first = controls[0], last = controls[controls.length - 1];
		if (event.shiftKey && (document.activeElement === first || document.activeElement === popup.current)) { event.preventDefault(); last.focus(); }
		else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
	}}>
		<Panel growContent disableScroll decorator="absolute" onClose={onClose} sx={{ padding: "0", width: "900px", maxWidth: "94vw", height: "88dvh", maxHeight: "88dvh", borderRadius: "12px", overflow: "hidden", backgroundColor: "#fff" }}>
			<Header>
				<div><div className="eyebrow">{label(msg("processingReconciliation"), msg("message_d11046e6"))}</div><h2>{msg("reco")}{id}</h2></div>
				<AdminButton variant="secondarySmall" size="small" className="close" onClick={onClose} title={closeLabel} aria-label={closeLabel}><X size={18} /></AdminButton>
			</Header>
			{busy ? <Body role="status">{label(msg("loadingRecoDetails"), msg("reco_f6a4e26b"))}</Body>
				: error ? <Body><div className="card"><p role="alert" className="error">{error}</p><AdminButton variant="secondarySmall" onClick={() => setAttempt(value => value + 1)}>{label(msg("retry"), msg("message_5189135a"))}</AdminButton></div></Body>
				: data == null ? <Body>{label(msg("noRecoDetailsReturned"), msg("reco_8efacdb9"))}</Body>
				: <TabArea><Tabs variant="modern" spaceBottom={0}>
					<TabItem title={label(msg("details"), msg("message_773190d8"))} icon={<TabIconInfo size={16} />}>
						<Body>
							<h3 className="section-label">{label("Record", msg("message_1d80e175"))}</h3>
							<div className="card"><dl className="fields">
								<dt>ID</dt><dd>{text(record?.id)}</dd>
								<dt>{msg("paymentId_d9c9afd8")}</dt><dd>{text(record?.payment_id)}</dd>
								<dt>{label(msg("created"), msg("message_73e85fb9"))}</dt><dd><span className="muted"><DateTimeTooltip value={typeof record?.created_at === "string" ? record.created_at : undefined} locale={i18n.language}>{dateText(record?.created_at, i18n.language)}</DateTimeTooltip></span></dd>
								<dt>{label(msg("period_6e795d4d"), msg("message_e97ca72e"))}</dt><dd>{String(record?.yyyymm ?? "").replace(/^(\d{4})(\d{2})$/, "$1-$2") || "—"}</dd>
								<dt>{label(msg("type"), msg("message_d4a6795c"))}</dt><dd>{text(record?.type)}</dd>
								<dt>{msg("fileId")}</dt><dd>{text(record?.file_id)}</dd>
							</dl></div>
							<h3 className="section-label">{label(msg("steps"), msg("message_c10e1abf"))} · {steps.length}</h3>
							{steps.length === 0 && <div className="card">{label(msg("noStepsReturned"), msg("message_e0de4f02"))}</div>}
							{steps.map((value, index) => {
								const step = object(value), response = object(step?.response), result = object(response?.data);
								const status = typeof result?.status === "string" ? result.status : null;
								const failed = response?.error != null;
								return <section className="card step" key={index}>
									<div className="step-head"><h3>{label(msg("step_8e6a6cca"), msg("message_1964891c"))} {text(step?.step ?? index + 1)}</h3>
										{status && <span className={`badge ${failed ? "failed" : status === "finished" || status === "active" ? status : ""}`}>{status}</span>}
										{failed && <span className="badge failed">{label(msg("error"), msg("message_bf22dfee"))}</span>}
									</div>
									<h4>{label(msg("request_59f03d64"), msg("message_e08bc1c8"))}</h4><Fields value={step?.request} />
									<h4>{label(msg("response_9061383b"), msg("message_5cfcac6a"))}</h4><Fields value={step?.response} />
									{typeof step?.xml === "string" && <details><summary><ChevronDown size={16} />{msg("xmlSoap")}</summary><Pre data={step.xml} label={msg("templateStepXml_52ae7760", { v0: text(step.step) })} wrap /></details>}
								</section>;
							})}
						</Body>
					</TabItem>
					<TabItem title={msg("raw_ac0562bb")} icon={<TabIconFileJson size={16} />}><Body><Pre data={data} label={msg("templateReco_2d457391", { v0: id })} wrap /></Body></TabItem>
				</Tabs></TabArea>}
		</Panel>
	</div>, document.body);
};
